/**
 * Product Card Slider
 * Handles image slider functionality on product cards
 */

class ProductCardSlider {
  constructor(element) {
    this.container = element;
    this.track = element.querySelector('.product-card__slider-track');
    this.slides = Array.from(element.querySelectorAll('.product-card__slider-slide'));
    this.dots = Array.from(element.querySelectorAll('.product-card__slider-dot'));
    
    if (!this.track || this.slides.length === 0) return;
    
    this.currentIndex = 0;
    this.autoScrollSpeed = parseInt(this.container.dataset.autoScrollSpeed) || 1500;
    this.autoScrollInterval = null;
    this.isMobile = window.innerWidth < 799;
    
    // Touch/swipe properties
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isDragging = false;
    
    this.init();
  }
  
  init() {
    // Show first slide
    this.goToSlide(0);
    
    // Set up event listeners
    this.setupEventListeners();
  }
  
  setupEventListeners() {
    // Desktop: auto-scroll on hover
    if (!this.isMobile) {
      this.container.addEventListener('mouseenter', () => this.startAutoScroll());
      this.container.addEventListener('mouseleave', () => this.stopAutoScroll());
    }
    
    // Mobile: swipe gestures
    if (this.isMobile) {
      this.track.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
      this.track.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: true });
      this.track.addEventListener('touchend', (e) => this.handleTouchEnd(e));
    }
    
    // Dot navigation (both mobile and desktop)
    this.dots.forEach(dot => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const index = parseInt(dot.dataset.slideIndex);
        this.goToSlide(index);
        
        // Stop auto-scroll if on desktop and restart
        if (!this.isMobile) {
          this.stopAutoScroll();
          this.startAutoScroll();
        }
      });
    });
    
    // Handle window resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.isMobile = window.innerWidth < 799;
      }, 250);
    });
  }
  
  goToSlide(index) {
    // Remove active class from all slides and dots
    this.slides.forEach(slide => slide.classList.remove('is-active'));
    this.dots.forEach(dot => dot.classList.remove('is-active'));
    
    // Add active class to current slide and dot
    if (this.slides[index]) {
      this.slides[index].classList.add('is-active');
    }
    if (this.dots[index]) {
      this.dots[index].classList.add('is-active');
    }
    
    this.currentIndex = index;
  }
  
  nextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }
  
  prevSlide() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }
  
  startAutoScroll() {
    if (this.autoScrollInterval) return;
    
    this.autoScrollInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoScrollSpeed);
  }
  
  stopAutoScroll() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
  }
  
  // Touch event handlers for mobile swiping
  handleTouchStart(e) {
    this.touchStartX = e.changedTouches[0].screenX;
    this.isDragging = true;
  }
  
  handleTouchMove(e) {
    if (!this.isDragging) return;
    this.touchEndX = e.changedTouches[0].screenX;
  }
  
  handleTouchEnd(e) {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.handleSwipe();
  }
  
  handleSwipe() {
    const swipeThreshold = 50; // minimum distance for a swipe
    const diff = this.touchStartX - this.touchEndX;
    
    if (Math.abs(diff) < swipeThreshold) return;
    
    if (diff > 0) {
      // Swiped left - next slide
      this.nextSlide();
    } else {
      // Swiped right - previous slide
      this.prevSlide();
    }
  }
  
  destroy() {
    this.stopAutoScroll();
  }
}

// Initialize all sliders on the page
function initProductCardSliders() {
  const sliders = document.querySelectorAll('[data-product-card-slider]');
  
  sliders.forEach(slider => {
    // Avoid initializing the same slider twice
    if (slider.productCardSliderInstance) return;
    
    slider.productCardSliderInstance = new ProductCardSlider(slider);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCardSliders);
} else {
  initProductCardSliders();
}

// Re-initialize when new products are loaded (for infinite scroll/load more)
document.addEventListener('turbo:load', initProductCardSliders);
document.addEventListener('shopify:section:load', initProductCardSliders);

// For Turbo theme's page transitions
if (typeof Turbo !== 'undefined') {
  document.addEventListener('turbo:render', initProductCardSliders);
}

// For themes using InstantClick or similar
if (window.InstantClick) {
  InstantClick.on('change', initProductCardSliders);
}

// Export for potential external use
window.ProductCardSlider = ProductCardSlider;
window.initProductCardSliders = initProductCardSliders;
